import type { EditorInput } from "@/components/Form/ComplexMarkdownField";
import type { AdvancedChipOption } from "@/components/Form/TiptapEditor/chipOptionsConfig";
import {
  SOURCE_ONLY_TYPES,
  isFlowOutputNode,
} from "@/components/SpotflowV2/constants";

export interface TypeChoice {
  data_type: string;
  is_list?: boolean;
}

export interface NodeConnector {
  name: string;
  label?: string;
  description?: string;
  definition?: { data_type?: string; is_list?: boolean };
  // V2 outputs: the declared shape of an object output (of each element for
  // a list), display-only.
  object_fields?: Array<{
    name?: string;
    label?: string | null;
    data_type?: string;
  }> | null;
  visible?: boolean;
  optional?: boolean;
  type?: string;
  keys?: string[];
  manual?: boolean;
  choices?: TypeChoice[];
  type_definitions?: Record<string, TypeChoice>;
  rules?: Array<Record<string, unknown>>;
}

export interface MentionBoundInput {
  bindingKey: string;
  sourceName: string;
  sourceOutput: string;
  sourceImage?: string;
  sourceIconName?: string;
  sourceColor?: string;
  sourceIsList: boolean;
  sourceDataType?: string;
  /** True when the binding source is a secret (workflow- or workspace-scoped). */
  isSecret?: boolean;
}

export interface BoundMentionOption {
  connectorName: string;
  bindingKey: string;
  slotKey: string;
  mentionId: string;
  label: string;
  dataType: string;
  isList: boolean;
  sourceName?: string;
  sourceImage?: string;
  sourceIconName?: string;
  sourceColor?: string;
  sourceOutput?: string;
  sourceKind?: "node" | "secret";
}

export const VAR_KEYS_CONFIG_KEY = "__var_keys";
export const VAR_TYPE_DEFS_CONFIG_KEY = "__var_type_defs";

export function isVariableConnector(input: { type?: string }): boolean {
  return (
    input.type === "variable_connector" ||
    input.type === "variable_type_size_connector"
  );
}

/**
 * Connector types whose `definition.data_type` is derived from a config field
 * (the runtime equivalent of the V1 `TypeSetBy` rule). V2 doesn't render the
 * data type on the handle itself, so the only callers that still care are the
 * picker (sort order, compatibility warnings) and list propagation. These
 * connector types are the exhaustive set, matching the backend `noxus.connector`
 * subclasses (`VariableTypeConnector`, `VariableTypeFlowInputConnector`,
 * `VariableTypeFlowOutputConnector`).
 */
export function isVariableTypedConnector(connector: {
  type?: string;
}): boolean {
  return (
    connector.type === "variable_type_connector" ||
    connector.type === "variable_type_input" ||
    connector.type === "variable_type_output"
  );
}

// OutputNodeV2's static output is declared `str`; only node_config.type
// carries the bound source's type.
export function usesConfigDrivenType(
  connector: { type?: string },
  nodeType: string | undefined | null
): boolean {
  return (
    isVariableTypedConnector(connector) ||
    (!!nodeType && SOURCE_ONLY_TYPES.has(nodeType)) ||
    isFlowOutputNode(nodeType)
  );
}

function isMentionVariableConnector(input: { type?: string }): boolean {
  return (
    input.type === "variable_connector" ||
    input.type === "variable_type_size_connector"
  );
}

export function getVarKeys(
  nodeConfig: Record<string, unknown>,
  connectorName: string
): string[] | null {
  const varKeys = nodeConfig[VAR_KEYS_CONFIG_KEY] as
    | Record<string, string[]>
    | undefined;
  if (!varKeys || !(connectorName in varKeys)) return null;
  return varKeys[connectorName] ?? [];
}

export function setVarKeys(
  nodeConfig: Record<string, unknown>,
  connectorName: string,
  keys: string[]
): Record<string, unknown> {
  const existing =
    (nodeConfig[VAR_KEYS_CONFIG_KEY] as Record<string, string[]> | undefined) ??
    {};
  return {
    ...nodeConfig,
    [VAR_KEYS_CONFIG_KEY]: { ...existing, [connectorName]: keys },
  };
}

export function getVarTypeDefs(
  nodeConfig: Record<string, unknown>,
  connectorName: string
): Record<string, TypeChoice> {
  const all = nodeConfig[VAR_TYPE_DEFS_CONFIG_KEY] as
    | Record<string, Record<string, TypeChoice>>
    | undefined;
  if (!all || !(connectorName in all)) return {};
  return all[connectorName] ?? {};
}

export function setVarTypeDefs(
  nodeConfig: Record<string, unknown>,
  connectorName: string,
  typeDefs: Record<string, TypeChoice>
): Record<string, unknown> {
  const existing =
    (nodeConfig[VAR_TYPE_DEFS_CONFIG_KEY] as
      | Record<string, Record<string, TypeChoice>>
      | undefined) ?? {};
  return {
    ...nodeConfig,
    [VAR_TYPE_DEFS_CONFIG_KEY]: { ...existing, [connectorName]: typeDefs },
  };
}

export function buildBoundMentionOptions({
  inputs,
  nodeConfig,
  variableBindings,
  boundInputs,
}: {
  inputs: NodeConnector[];
  nodeConfig: Record<string, unknown>;
  variableBindings: Record<string, string | undefined>;
  boundInputs: MentionBoundInput[];
}): BoundMentionOption[] {
  const mentionConnectors = inputs.filter(isMentionVariableConnector);
  if (mentionConnectors.length === 0) return [];

  const useSimpleIds = mentionConnectors.length === 1;
  const boundByBindingKey = new Map(
    boundInputs.map((bound) => [bound.bindingKey, bound] as const)
  );
  const options: BoundMentionOption[] = [];

  for (const connector of mentionConnectors) {
    const storedKeys = getVarKeys(nodeConfig, connector.name);
    const keys = storedKeys !== null ? storedKeys : (connector.keys ?? []);
    const storedTypeDefs = getVarTypeDefs(nodeConfig, connector.name);
    const registryTypeDefs = connector.type_definitions ?? {};
    const connectorDefaultType = connector.definition?.data_type ?? "object";
    const connectorDefaultIsList = connector.definition?.is_list ?? false;

    for (const [keyIndex, key] of keys.entries()) {
      const keyedBindingKey = `${connector.name}__${key}`;
      const legacyBindingKey = connector.name;
      const bindingKey =
        (variableBindings[keyedBindingKey] ??
        (keyIndex === 0 ? variableBindings[legacyBindingKey] : undefined))
          ? variableBindings[keyedBindingKey]
            ? keyedBindingKey
            : legacyBindingKey
          : null;

      if (!bindingKey) continue;

      const bound =
        boundByBindingKey.get(bindingKey) ??
        boundByBindingKey.get(keyedBindingKey) ??
        (keyIndex === 0 ? boundByBindingKey.get(legacyBindingKey) : undefined);
      const typeDef: TypeChoice = storedTypeDefs[key] ??
        registryTypeDefs[key] ?? {
          data_type: connectorDefaultType,
          is_list: connectorDefaultIsList,
        };
      // Use the actual key name as the placeholder identifier so the markdown
      // value (`:var[<key>]`) matches the runtime variable connector key.
      // Backend can then resolve placeholders via a direct dict lookup with
      // no position/case translation.
      const mentionId = useSimpleIds ? key : `${connector.name}__${key}`;
      const label = bound?.sourceOutput ?? key;

      options.push({
        connectorName: connector.name,
        bindingKey,
        slotKey: key,
        mentionId,
        label,
        dataType: bound?.sourceDataType ?? typeDef.data_type,
        isList: bound?.sourceIsList ?? !!typeDef.is_list,
        sourceName: bound?.sourceName,
        sourceImage: bound?.sourceImage,
        sourceIconName: bound?.sourceIconName,
        sourceColor: bound?.sourceColor,
        sourceOutput: bound?.sourceOutput,
        sourceKind: bound?.isSecret ? "secret" : "node",
      });
    }
  }

  return options;
}

export function boundMentionsToEditorInputs(
  options: BoundMentionOption[]
): EditorInput[] {
  return options.map((option) => ({
    id: option.mentionId,
    name: option.label,
    dataType: option.dataType,
    isList: option.isList,
    sourceName: option.sourceName,
    sourceImage: option.sourceImage,
    sourceIconName: option.sourceIconName,
    sourceColor: option.sourceColor,
    sourceOutput: option.sourceOutput,
    sourceKind: option.sourceKind,
  }));
}

export function boundMentionsToChipOptions(
  options: BoundMentionOption[]
): AdvancedChipOption[] {
  if (options.length === 0) return [];
  return [
    {
      label: "Values",
      color: "blue",
      values: options.map((option) => ({
        value: option.slotKey,
        label: option.label,
        sourceImage: option.sourceImage,
        sourceIconName: option.sourceIconName,
        sourceColor: option.sourceColor,
        sourceName: option.sourceName,
        sourceKind: option.sourceKind,
        dataType: option.dataType,
        isList: option.isList,
      })),
    },
  ];
}
